import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { invalidateToolListingStatus, useToolListingStatus } from '@/hooks/useToolListingStatus';
import {
  AppDialogSection,
  AppDialog,
  AppDialogContent,
  AppDialogHeader,
  AppDialogTitle,
  AppDialogActions,
  AppDialogXBtn,
} from '@/UI/AppDialog/AppDialog';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { I18N_KEYS } from '@/language/keys';
import { AuthContext } from '@/contexts/AuthContext';
import { Flex, FlexCenVer, FlexCol } from '@/UI/styles';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { InfoIcon } from '@/UI/Icons/InfoIcon';
import { useGlobalStore } from '@/state/global.state';
import {
  usePublishToCommunityFormStore,
  type PublishImage,
  type PublishToCommunityForm,
} from '@/state/publishToCommunityForm.state';
import { useLinkedFigmaAccount } from '@/hooks/useLinkedFigmaAccount';
import { useToolViewQuery } from '@/queries/useToolView';
import {
  FF_TOOL_COMMUNITY_PUBLISHING_HIDDEN_CATEGORY,
  FF_WORKFLOW_COMMUNITY_PUBLISHING,
  FF_WORKFLOW_COMMUNITY_PUBLISHING_WEAVE_CATEGORY_ALLOWLIST,
} from '@/consts/featureFlags';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { EXTERNAL_LINKS } from '@/consts/externalLinks.consts';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES } from '@/consts/analytics.consts';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import { log } from '@/logger/logger.ts';
import {
  type CommunityCarouselMedia,
  type CommunityCategory,
  type CommunityResourceType,
  type PublisherProfile,
  createPublisherProfile,
  fetchCommunityCategories,
  publishToolToCommunity,
  publishWorkflowToCommunity,
  uploadCommunityImages,
  uploadCommunityToolImages,
} from '@/api/workflowCommunityPublishing.api';
import { prepareCommunityPublishSnapshot } from '@/api/workflowCommunityPublishSnapshot.api';
import { type OrgPublishResult, publishToolToOrg } from '@/api/figmaOrgPublishing.api';
import {
  EMPTY_PUBLISHER_PROFILES_RESPONSE,
  communityPublisherProfilesQueryKey,
  useCommunityPublisherProfiles,
} from '@/hooks/useCommunityPublisherProfiles';
import { getCommunityPublishEligibility } from '@/utils/communityPublishingEligibility.utils';
import { ICON_MAX_LONG_EDGE, shrinkImageToFit } from '@/utils/shrinkImageToFit';
import PublishToCommunityModalDescription, {
  DESCRIPTION_MAX,
  NAME_MAX,
  TAGS_MAX,
} from './PublishToCommunityModalDescription';
import PublishToCommunityModalImages from './PublishToCommunityModalImages';
import PublishToCommunityModalDetails from './PublishToCommunityModalDetails';
import PublishToCommunityModalTerminal from './PublishToCommunityModalTerminal';
import {
  invalidateToolCommunityMetadata,
  invalidateWorkflowCommunityMetadata,
  seedPendingToolCommunityMetadata,
  seedPendingWorkflowCommunityMetadata,
  useToolCommunityMetadata,
  useWorkflowCommunityMetadata,
} from './useWorkflowCommunityMetadata';
import { useCommunityLinkActions } from './useCommunityLinkActions';
import { isPublishedToCommunity } from './workflowCommunityStatus';
import { usePublishWorkflowSource } from './publishWorkflowSource';
import {
  getCommunityPublishErrorPayload,
  type WorkflowCommunityPublishMode,
  type WorkflowCommunityPublishSource,
  type WorkflowCommunityPublishTrackingContext,
} from './workflowCommunityPublishAnalytics';
import type { HandleStatus } from './CommunityHandleSection';
import type { OrgPublishDestination } from '@/hooks/useOrgPublishDestinations';

const logger = log.getLogger('PublishToCommunityModal');

enum Step {
  Description = 'description',
  Images = 'images',
  Details = 'details',
  Publishing = 'publishing',
  Success = 'success',
  Failure = 'failure',
}

const isTerminalStep = (s: Step): s is Step.Publishing | Step.Success | Step.Failure =>
  s === Step.Publishing || s === Step.Success || s === Step.Failure;

const AI_WORKFLOWS_PARENT_URL_SLUG = 'ai-workflows';
const WEAVY_CATEGORY_URL_SLUG = 'weavy';
// Tools publish under "Design tools", not the workflow "AI workflows" categories.
const DESIGN_TOOLS_PARENT_URL_SLUG = 'design-tools';
/**
 * Tools filed under this category are denied view in Community by `DenyHiddenCategoryWeaveAppView`,
 * so it's an internal lane — gated in the picker by {@link FF_TOOL_COMMUNITY_PUBLISHING_HIDDEN_CATEGORY}.
 * Keyed on the id rather than the slug so the picker and the policy share one source of truth: this
 * is the same literal the policy compiles in (`HIDDEN_WEAVE_CATEGORY_ID` in
 * `permissions/lib/functions.ts`). The row only exists in production, so outside production the
 * option never appears — which matches the policy, whose deny never fires there either.
 */
const HIDDEN_TOOL_CATEGORY_ID = '2f667c7b-e43b-418f-9008-15e902431c8e';

type FormErrors = {
  name: string | null;
  description: string | null;
  category: string | null;
  image: string | null;
  icon: string | null;
  terms: string | null;
  handle: string | null;
};

interface PublishToCommunityModalProps {
  open: boolean;
  onClose: () => void;
  source?: WorkflowCommunityPublishSource;
  mode?: WorkflowCommunityPublishMode;
  /**
   * When set, publish the underlying workflow to Community as a Tool (`weave_app`) instead of a
   * workflow (`weave_workflow`). `weaveAppId` is the tool's `weave_app_id` (= its WorkflowTool.id) —
   * the identity the Community open+run path resolves the tool by. Omit to publish as a workflow.
   */
  tool?: { weaveAppId: string };
  /**
   * Orgs a tool can be published to instead of Community. The first is selected by default and the
   * Details step lets the publisher switch when there is more than one.
   */
  orgDestinations?: OrgPublishDestination[];
}

const NO_ORG_DESTINATIONS: OrgPublishDestination[] = [];

type OrgPublishFailureStage = 'image_upload' | 'snapshot_prepare' | 'org_publish';

/** Stable identity so an unfetched categories query doesn't re-run every memo and effect below. */
const EMPTY_CATEGORIES: CommunityCategory[] = [];

const titleCollator = new Intl.Collator(undefined, { sensitivity: 'base' });
const sortByTitle = (categories: CommunityCategory[]) =>
  categories.sort((a, b) => titleCollator.compare(a.title, b.title));

const isHiddenToolCategory = (category: CommunityCategory): boolean => category.id === HIDDEN_TOOL_CATEGORY_ID;

/**
 * Sinatra rejects a publish under a category that omits the type, so one that would fail is never
 * offered. A missing field counts as accepting: reading it as "accepts nothing" would empty the
 * picker, which flips `canPublishWithoutCategory` on and publishes with no category at all.
 */
const acceptsResourceType = (category: CommunityCategory, resourceType: CommunityResourceType): boolean =>
  !Array.isArray(category.allowed_resource_types) || category.allowed_resource_types.includes(resourceType);

/**
 * Refetches a tool's thumbnail so it can seed the form as a new upload: the form's `existing` image
 * variant needs a Community sha1, which a tool row has no equivalent of. Resolves null on any
 * failure, cross-origin included — a seeded form the user can complete beats a modal that won't open.
 */
const fetchThumbnailAsFile = async (url: string): Promise<File | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    if (!blob.type.startsWith('image/')) return null;
    return new File([blob], `thumbnail.${blob.type.slice('image/'.length)}`, { type: blob.type });
  } catch {
    return null;
  }
};

export const getPublishableCommunityCategories = (
  rawCategories: CommunityCategory[],
  {
    showWeavyCategory = false,
    isPublishingAsTool = false,
    showHiddenToolCategory = false,
  }: { showWeavyCategory?: boolean; isPublishingAsTool?: boolean; showHiddenToolCategory?: boolean } = {},
): CommunityCategory[] => {
  if (isPublishingAsTool) {
    // A tool publishes under "Design tools" — the design-tools category itself plus its children.
    // A missing parent leaves `parentId` undefined, which matches nothing. The slug lookup reads the
    // unfiltered list, so scoping resolves even when the parent itself doesn't accept a tool.
    const parentId = rawCategories.find((c) => c.url_slug === DESIGN_TOOLS_PARENT_URL_SLUG)?.id;
    return sortByTitle(
      rawCategories.filter(
        (c) =>
          acceptsResourceType(c, 'weave_app') &&
          (isHiddenToolCategory(c) ? showHiddenToolCategory : c.id === parentId || c.parent_category_id === parentId),
      ),
    );
  }

  const aiWorkflowsParent = rawCategories.find((c) => c.url_slug === AI_WORKFLOWS_PARENT_URL_SLUG);
  return sortByTitle(
    rawCategories.filter(
      (c) =>
        acceptsResourceType(c, 'weave_workflow') &&
        ((aiWorkflowsParent && c.parent_category_id === aiWorkflowsParent.id) ||
          (showWeavyCategory && c.url_slug === WEAVY_CATEGORY_URL_SLUG)),
    ),
  );
};

const PublishToCommunityModal = ({
  open,
  onClose,
  source,
  mode,
  tool,
  orgDestinations = NO_ORG_DESTINATIONS,
}: PublishToCommunityModalProps) => {
  const isPublishingAsTool = tool != null;
  const [pickedOrgPlanId, setPickedOrgPlanId] = useState<string | null>(null);
  // Who Figma credits on the org listing and where to open the tool, from the last successful org publish.
  const [orgPublishResult, setOrgPublishResult] = useState<OrgPublishResult | null>(null);
  const selectedOrg = tool
    ? (orgDestinations.find((org) => org.planId === pickedOrgPlanId) ?? orgDestinations.at(0) ?? null)
    : null;
  const selectedOrgPlanId = selectedOrg?.planId;
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const { currentUser } = useContext(AuthContext);
  const { updateSnackbarData } = useGlobalStore();
  const queryClient = useQueryClient();
  const workflow = usePublishWorkflowSource();
  const workflowId = workflow.id;
  // Draft-form state is keyed by the resource being published, not by the workflow: a tool and its
  // workflow are separate Community listings, so sharing one key let whichever was seeded first
  // (name, description, images) show up in the other's form.
  const formKey = tool ? tool.weaveAppId : workflowId;
  // A tool reads its published state under its own `weave_app_id` (separate query key); a workflow
  // reads under the workflow id. Both hooks are called unconditionally (rules of hooks) but each is
  // disabled when its id is undefined, so only the active one fetches.
  const { data: workflowMetadata, isFetched: isWorkflowMetadataFetched } = useWorkflowCommunityMetadata(
    tool ? undefined : workflowId,
  );
  const { data: toolListing, isOrgScopedPublishingEnabled } = useToolListingStatus(tool?.weaveAppId);
  const shouldFetchToolCommunityMetadata =
    !isOrgScopedPublishingEnabled || toolListing?.destination?.type === 'community';
  const { data: toolMetadata, isFetched: isToolMetadataFetched } = useToolCommunityMetadata(
    shouldFetchToolCommunityMetadata ? tool?.weaveAppId : undefined,
  );
  // The tool's own row — the only seed source before a first publish, when there is no metadata yet.
  const { data: toolView } = useToolViewQuery(tool?.weaveAppId, open && isPublishingAsTool);
  const communityMetadata = tool ? toolMetadata : workflowMetadata;
  const isCommunityMetadataFetched = tool ? isToolMetadataFetched : isWorkflowMetadataFetched;
  const publishMode: WorkflowCommunityPublishMode =
    mode ?? (isPublishedToCommunity(communityMetadata) ? 'update' : 'initial');
  // Existing listings retain their icon when an update omits it. Org listings are reported by the
  // management read, not Community metadata. Use confirmed listing state rather than caller-supplied mode.
  const isPublishedTool = Boolean(
    tool &&
    ((toolMetadata && !toolMetadata.unpublished_at && toolMetadata.current_version) ||
      toolListing?.destination?.type === 'org'),
  );
  const iconRequired = isPublishingAsTool && !isPublishedTool;
  const trackingContext = useMemo<WorkflowCommunityPublishTrackingContext | undefined>(
    () =>
      source
        ? {
            source,
            surface: tool ? ('tool' as const) : ('workflow' as const),
            mode: publishMode,
            destination: selectedOrgPlanId ? ('org' as const) : ('community' as const),
            ...(selectedOrgPlanId ? { orgPlanId: selectedOrgPlanId } : {}),
            workflowId,
            ...(tool ? { toolId: tool.weaveAppId } : {}),
            resourceId: communityMetadata?.resource_id ?? null,
          }
        : undefined,
    [communityMetadata?.resource_id, publishMode, selectedOrgPlanId, source, tool, workflowId],
  );
  const trackPublishEvent = useCallback(
    (eventName: string, payload: Record<string, unknown> = {}) => {
      if (!trackingContext) return;
      track(
        eventName,
        {
          ...trackingContext,
          ...payload,
        },
        TrackTypeEnum.Product,
      );
    },
    [track, trackingContext],
  );
  const workflowRole = workflow.role;
  const isFplMigrationEnabled = useFplMigrationFlag();
  const { value: isPublishToCommunityEnabled } = useFeatureFlag(FF_WORKFLOW_COMMUNITY_PUBLISHING);
  const { value: isWeavyCategoryAllowed } = useFeatureFlag(FF_WORKFLOW_COMMUNITY_PUBLISHING_WEAVE_CATEGORY_ALLOWLIST);
  const { value: isHiddenToolCategoryAllowed } = useFeatureFlag(FF_TOOL_COMMUNITY_PUBLISHING_HIDDEN_CATEGORY);
  // Needs the email: it prefills the publisher-profile form below.
  const { data: figmaConnection } = useLinkedFigmaAccount({ includeEmail: true });
  const figmaUserId = figmaConnection?.connected ? figmaConnection.figmaUserId : null;
  const figmaEmail = figmaConnection?.connected ? (figmaConnection.figmaEmail ?? '') : '';

  const initialForm = useMemo<PublishToCommunityForm>(
    () => ({
      name: workflow.name.slice(0, NAME_MAX),
      description: '',
      category: '',
      tags: [],
      thumbnail: null,
      icon: null,
      carouselImages: [],
      supportEmail: currentUser?.email ?? '',
      agreedToTerms: false,
    }),
    [workflow.name, currentUser?.email],
  );

  const setFormAction = usePublishToCommunityFormStore((s) => s.setForm);
  const stored = usePublishToCommunityFormStore((s) => s.forms[formKey]);
  const hasSeededForm = usePublishToCommunityFormStore((s) => s.seededFormKeys[formKey] ?? false);
  const markFormSeeded = usePublishToCommunityFormStore((s) => s.markFormSeeded);
  const form = useMemo<PublishToCommunityForm>(() => ({ ...initialForm, ...stored }), [initialForm, stored]);

  const [step, setStep] = useState<Step>(Step.Description);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [step]);
  const [errors, setErrors] = useState<FormErrors>({
    name: null,
    description: null,
    category: null,
    image: null,
    icon: null,
    terms: null,
    handle: null,
  });
  const [handle, setHandle] = useState('');
  const [handleStatus, setHandleStatus] = useState<HandleStatus>({ kind: 'empty' });
  const [createdProfile, setCreatedProfile] = useState<PublisherProfile | null>(null);
  const {
    data: publisherProfileState = EMPTY_PUBLISHER_PROFILES_RESPONSE,
    error: publisherProfilesError,
    refetch: refetchPublisherProfiles,
  } = useCommunityPublisherProfiles(figmaUserId, open && isPublishToCommunityEnabled === true);
  // Under the user-level rollout the agreement is asked once per Figma user; until then, every publish
  // asks. The same decision drives the checkbox and its validation.
  const requiresCreatorAgreement =
    !publisherProfileState.community_user_level_creator_agreement_enabled ||
    !publisherProfileState.has_accepted_community_creator_agreement;
  // Org publishing doesn't use categories, so only fetch them for Community publishing.
  const categoriesQueryEnabled = !selectedOrg && (figmaConnection?.connected ?? false);
  const {
    data: rawCategories = EMPTY_CATEGORIES,
    isLoading: rawCategoriesLoading,
    error: categoriesError,
  } = useQuery({
    queryKey: ['communityCategories'],
    queryFn: fetchCommunityCategories,
    staleTime: Infinity,
    enabled: categoriesQueryEnabled,
  });
  // A disabled query reports isLoading === false with no data, which is indistinguishable from
  // "Community has no categories" — and that reading drives a destructive branch below. Treat
  // not-yet-fetched as loading so nothing acts on an empty list we never asked for.
  const categoriesLoading = rawCategoriesLoading || !categoriesQueryEnabled;
  useEffect(() => {
    if (selectedOrg || !categoriesError) return;
    logger.error('Failed to fetch community categories:', categoriesError);
    if (open) {
      updateSnackbarData({
        text: 'Failed to load categories, please refresh and try again',
        isOpen: true,
      });
    }
  }, [categoriesError, open, selectedOrg, updateSnackbarData]);
  const offerableCategories = useMemo(
    () =>
      getPublishableCommunityCategories(rawCategories, {
        showWeavyCategory: isWeavyCategoryAllowed === true,
        isPublishingAsTool,
        showHiddenToolCategory: isHiddenToolCategoryAllowed === true,
      }),
    [isWeavyCategoryAllowed, rawCategories, isPublishingAsTool, isHiddenToolCategoryAllowed],
  );
  // A category the tool is already filed under must stay resolvable in the options even when it's
  // gated out, or the picker drops it, the default-category effect swaps in a visible one, and
  // re-publishing un-hides the tool. Both the draft form and the published metadata are retained,
  // not one or the other: seedPendingToolCommunityMetadata omits `category_id`, so right after a
  // publish the form is the only record of the choice, while a form holding some other id must not
  // make the published choice unresolvable.
  const categories = useMemo(() => {
    const retainedIds = [...new Set([form.category, communityMetadata?.category_id])].filter(
      (id): id is string => !!id && !offerableCategories.some((c) => c.id === id),
    );
    if (retainedIds.length === 0) return offerableCategories;
    const retained = retainedIds
      .map((id) => rawCategories.find((c) => c.id === id))
      .filter((c): c is CommunityCategory => c !== undefined);
    return retained.length > 0 ? sortByTitle([...offerableCategories, ...retained]) : offerableCategories;
  }, [offerableCategories, rawCategories, form.category, communityMetadata?.category_id]);
  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === form.category) ?? null,
    [categories, form.category],
  );
  const hasPublishableCategories = categories.length > 0;
  const canPublishWithoutCategory = !categoriesLoading && !categoriesError && !hasPublishableCategories;
  const categoryRequired = !selectedOrg && !canPublishWithoutCategory;
  const publishVersionRef = useRef(0);
  const publishInFlightRef = useRef(false);
  const hasTrackedOpenRef = useRef(false);

  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState<string>('');
  useLayoutEffect(() => {
    const thumbnail = form.thumbnail;
    if (!thumbnail) {
      setThumbnailPreviewUrl('');
      return;
    }
    if (thumbnail.kind === 'existing') {
      setThumbnailPreviewUrl(thumbnail.url);
      return;
    }
    const url = URL.createObjectURL(thumbnail.file);
    setThumbnailPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [form.thumbnail]);

  const [iconPreviewUrl, setIconPreviewUrl] = useState<string>('');
  useLayoutEffect(() => {
    const icon = form.icon;
    if (!icon) {
      setIconPreviewUrl('');
      return;
    }
    if (icon.kind === 'existing') {
      setIconPreviewUrl(icon.url);
      return;
    }
    const url = URL.createObjectURL(icon.file);
    setIconPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [form.icon]);

  const [carouselPreviewUrls, setCarouselPreviewUrls] = useState<string[]>([]);
  useLayoutEffect(() => {
    const createdUrls: string[] = [];
    const urls = form.carouselImages.map((image) => {
      if (image.kind === 'existing') return image.url;
      const url = URL.createObjectURL(image.file);
      createdUrls.push(url);
      return url;
    });
    setCarouselPreviewUrls(urls);
    return () => {
      for (const url of createdUrls) {
        URL.revokeObjectURL(url);
      }
    };
  }, [form.carouselImages]);

  useEffect(() => {
    if (publisherProfilesError) logger.error('Failed to fetch publisher profiles:', publisherProfilesError);
  }, [publisherProfilesError]);

  // Picks a category only for a genuinely fresh form. It is gated on `open` because the modal is
  // mounted while closed (`open={isModalOpen}`), and the seeding effect below is not — left ungated,
  // this would write a visible category before seeding ever runs, which then makes the published
  // category unresolvable and silently un-hides a hidden tool on the next publish.
  useEffect(() => {
    if (!open || selectedOrg || categoriesLoading || categoriesError) return;
    if (canPublishWithoutCategory) {
      // Temporarily allow an empty category so design partners can pre-seed workflows to Community
      // before the new AI workflow categories are enabled.
      if (form.category || form.tags.length > 0) {
        setFormAction(formKey, { category: '', tags: [] });
      }
      setErrors((prev) => (prev.category ? { ...prev, category: null } : prev));
      return;
    }
    if (selectedCategory) return;
    // An already-published category belongs to the seeding effect, so never default over it. This
    // also covers a published id that no longer resolves in the options (its row was disabled, say):
    // replacing it here would downgrade a hidden tool to a visible category, whereas leaving it
    // blocks publishing on the category error until the user picks one deliberately. Applies when
    // seeding bails too — unpublished, or no current version.
    if (communityMetadata?.category_id) return;
    // Never default to the hidden lane — publishing a tool hidden has to be a deliberate choice.
    // If the hidden lane is somehow the only option, set nothing rather than pick it.
    const defaultCategory = categories.find((c) => !isHiddenToolCategory(c));
    if (!defaultCategory) return;
    setFormAction(formKey, { category: defaultCategory.id, tags: [] });
  }, [
    open,
    selectedOrg,
    categories,
    categoriesLoading,
    categoriesError,
    canPublishWithoutCategory,
    communityMetadata?.category_id,
    form.category,
    form.tags.length,
    selectedCategory,
    formKey,
    setFormAction,
  ]);

  useEffect(() => {
    if (open) setStep(Step.Description);
  }, [open]);

  const userSetForm = useCallback(
    (update: Parameters<typeof setFormAction>[1]) => {
      markFormSeeded(formKey);
      setFormAction(formKey, update);
    },
    [formKey, markFormSeeded, setFormAction],
  );

  useEffect(() => {
    if (!open) {
      hasTrackedOpenRef.current = false;
      return;
    }
    if (hasTrackedOpenRef.current) return;
    hasTrackedOpenRef.current = true;
    trackPublishEvent(WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.MODAL_OPENED, {
      metadataLoaded: isCommunityMetadataFetched,
      publishingStatus: communityMetadata?.publishing_status ?? null,
    });
  }, [communityMetadata?.publishing_status, isCommunityMetadataFetched, open, trackPublishEvent]);

  const setField = useCallback(
    <K extends keyof PublishToCommunityForm>(field: K, value: PublishToCommunityForm[K]) => {
      userSetForm({ [field]: value });
    },
    [userSetForm],
  );

  const clearError = (field: keyof FormErrors) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: null } : prev));
  };

  const toggleTag = (tag: string) => {
    userSetForm((current) => {
      const tags = current.tags ?? initialForm.tags;
      if (tags.includes(tag)) return { tags: tags.filter((t) => t !== tag) };
      if (tags.length >= TAGS_MAX) return {};
      return { tags: [...tags, tag] };
    });
  };

  const handleNameChange = (v: string) => {
    setField('name', v.slice(0, NAME_MAX));
    clearError('name');
  };
  const handleDescriptionChange = (v: string) => {
    setField('description', v.slice(0, DESCRIPTION_MAX));
    clearError('description');
  };
  const handleAgreedToTermsChange = (v: boolean) => {
    setField('agreedToTerms', v);
    clearError('terms');
  };
  const handleThumbnailFile = (file: File) => {
    setField('thumbnail', { kind: 'new', id: uuidv4(), file });
    clearError('image');
  };

  const handleThumbnailRemove = () => {
    setField('thumbnail', null);
  };

  const handleIconFile = (file: File) => {
    setField('icon', { kind: 'new', id: uuidv4(), file });
    clearError('icon');
  };

  const handleIconRemove = () => {
    setField('icon', null);
  };

  const handleCarouselFile = (index: number, file: File) => {
    userSetForm((current) => {
      const carouselImages = current.carouselImages ?? initialForm.carouselImages;
      const next = [...carouselImages];
      next[index] = { kind: 'new', id: uuidv4(), file };
      return { carouselImages: next };
    });
  };

  const handleCarouselRemove = (index: number) => {
    userSetForm((current) => {
      const carouselImages = current.carouselImages ?? initialForm.carouselImages;
      return { carouselImages: carouselImages.filter((_, i) => i !== index) };
    });
  };

  const handleCarouselReorder = (images: PublishImage[]) => {
    setField('carouselImages', images);
  };
  const handleTermsClick = () => {
    window.open(EXTERNAL_LINKS.figmaCommunityTerms, '_blank');
  };

  const getFormTrackingPayload = useCallback(
    () => ({
      thumbnailImageCount: form.thumbnail ? 1 : 0,
      carouselImageCount: form.carouselImages.length,
      tagCount: form.tags.length,
      hasCategory: Boolean(selectedCategory),
      hasExistingPublisherProfile: Boolean(createdProfile ?? publisherProfileState.profiles[0]),
    }),
    [
      createdProfile,
      form.carouselImages.length,
      form.tags.length,
      form.thumbnail,
      publisherProfileState,
      selectedCategory,
    ],
  );

  const trackStepCompleted = useCallback(
    (completedStep: Step.Description | Step.Images, nextStep: Step.Images | Step.Details) => {
      trackPublishEvent(WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.STEP_COMPLETED, {
        ...getFormTrackingPayload(),
        step: completedStep,
        nextStep,
      });
    },
    [getFormTrackingPayload, trackPublishEvent],
  );

  const trackValidationFailed = useCallback(
    (validationStep: Step.Description | Step.Images | Step.Details, fieldKeys: Array<keyof FormErrors>) => {
      trackPublishEvent(WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.VALIDATION_FAILED, {
        ...getFormTrackingPayload(),
        step: validationStep,
        fieldKeys,
      });
    },
    [getFormTrackingPayload, trackPublishEvent],
  );

  const trackPublishResult = useCallback(
    (result: 'success' | 'failure', payload: Record<string, unknown> = {}) => {
      trackPublishEvent(WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.RESULT, {
        ...getFormTrackingPayload(),
        result,
        ...payload,
      });
    },
    [getFormTrackingPayload, trackPublishEvent],
  );

  const validateDescription = (): boolean => {
    const next: Partial<FormErrors> = {};
    if (!form.name.trim()) {
      next.name = t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.ERRORS.NAME_REQUIRED);
    }
    if (!form.description.trim()) {
      next.description = t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.ERRORS.DESCRIPTION_REQUIRED);
    }
    if (categoryRequired && !selectedCategory) {
      next.category = t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.ERRORS.CATEGORY_REQUIRED);
    }
    if (Object.keys(next).length > 0) {
      setErrors((prev) => ({ ...prev, ...next }));
      trackValidationFailed(Step.Description, Object.keys(next) as Array<keyof FormErrors>);
      return false;
    }
    return true;
  };

  const validateImages = (): boolean => {
    const next: Partial<FormErrors> = {};
    if (!form.thumbnail) {
      next.image = t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.ERRORS.IMAGE_REQUIRED);
    }
    // A Community tool listing is rendered with its icon, so a tool can't be published without one.
    // Workflows have no icon field at all.
    //
    // Only a first publish is gated. On an update the form isn't seeded with the icon the tool was
    // published with, so requiring one here would trap the user on this step; the publish omits
    // `icon_sha1` and the server carries the previous version's icon forward.
    if (iconRequired && !form.icon) {
      next.icon = t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.ERRORS.ICON_REQUIRED);
    }
    if (Object.keys(next).length === 0) return true;
    trackValidationFailed(Step.Images, Object.keys(next) as Array<keyof FormErrors>);
    setErrors((prev) => ({ ...prev, ...next }));
    return false;
  };

  const validateDetails = (): boolean => {
    // The Creator Agreement is a Community term; the org publish route neither takes nor records it.
    if (selectedOrg) return true;
    if (!requiresCreatorAgreement || form.agreedToTerms) return true;
    trackValidationFailed(Step.Details, ['terms']);
    setErrors((prev) => ({
      ...prev,
      terms: t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.ERRORS.TERMS_REQUIRED),
    }));
    return false;
  };

  const resetState = () => {
    setStep(Step.Description);
    setCreatedProfile(null);
    setHandle('');
    setHandleStatus({ kind: 'empty' });
    setErrors((prev) => ({ ...prev, handle: null }));
  };

  const handleClose = () => {
    if (step === Step.Publishing) {
      publishVersionRef.current += 1;
    }
    onClose();
    if (isFplMigrationEnabled) {
      resetState();
    }
  };

  const terminalTrackingContext = useMemo(
    () =>
      trackingContext
        ? {
            ...trackingContext,
            source: 'publish_modal_terminal' as const,
          }
        : undefined,
    [trackingContext],
  );
  const { copyLink, goToCommunity } = useCommunityLinkActions(
    communityMetadata?.community_url ?? null,
    terminalTrackingContext,
  );
  const openInFigmaUrl = orgPublishResult?.openInFigmaUrl ?? null;
  const { copyLink: copyOpenInFigmaLink, goToCommunity: openInFigma } = useCommunityLinkActions(openInFigmaUrl);

  useEffect(() => {
    if (!open || categoriesLoading || categoriesError || hasSeededForm) return;
    const metadata = communityMetadata;
    if (!metadata || metadata.unpublished_at || !metadata.current_version) return;

    const media = metadata.carousel_media ?? [];
    const toImage = (entry: CommunityCarouselMedia): PublishImage => ({
      kind: 'existing',
      id: uuidv4(),
      sha1: entry.sha1,
      url: entry.url,
    });
    // Keep the published id even when it doesn't resolve in the options — a category row that went
    // missing (disabled in admin, say) must not silently downgrade a hidden tool to a visible
    // category. Publishing stays blocked on the category error until the user picks one.
    const seededCategory =
      metadata.category_id != null ? categories.find((c) => c.id === metadata.category_id) : undefined;
    const seededCategoryId = seededCategory?.id ?? metadata.category_id ?? '';
    const validTagIds = new Set(seededCategory?.tags?.map((t) => t.id) ?? []);
    setFormAction(formKey, {
      name: (metadata.name || workflow.name).slice(0, NAME_MAX),
      description: (metadata.description ?? '').slice(0, DESCRIPTION_MAX),
      category: seededCategoryId,
      tags: (metadata.tags ?? []).filter((id) => validTagIds.has(id)),
      supportEmail: metadata.support_contact ?? '',
      thumbnail: media.length > 0 ? toImage(media[0]) : null,
      carouselImages: media.slice(1).map(toImage),
    });
    markFormSeeded(formKey);
  }, [
    open,
    categoriesLoading,
    categoriesError,
    hasSeededForm,
    communityMetadata,
    categories,
    workflow.name,
    formKey,
    setFormAction,
    markFormSeeded,
  ]);

  /**
   * Guards the deferred thumbnail write below. Scoped to the modal being open rather than to that
   * effect's own cleanup: `markFormSeeded` flips `hasSeededForm`, which the effect depends on, so its
   * cleanup fires on the very next commit and would abort the fetch every time.
   */
  const toolSeedCancelled = useRef(false);
  useEffect(() => {
    toolSeedCancelled.current = false;
    return () => {
      toolSeedCancelled.current = true;
    };
  }, [open]);

  // A tool with no Community listing yet seeds from its own row instead. The thumbnail fills both
  // the listing image and the icon; either can still be replaced in the form.
  useEffect(() => {
    if (!open || hasSeededForm || !isPublishingAsTool || !toolView) return;
    // Never over a live listing — the published values above are the newer ones.
    // The management read can establish that there is no Community metadata to fetch.
    const isMetadataResolved =
      isCommunityMetadataFetched || (toolListing != null && toolListing.destination?.type !== 'community');
    if (!isMetadataResolved || isPublishedToCommunity(communityMetadata)) return;
    // `isPublishedToCommunity` is an allowlist, so it is *not* the complement of the listing effect's
    // guard: a blocked listing keeps `unpublished_at` null and a `current_version`, which seeds there
    // and fails the check here. Both effects would then run off the same render's `hasSeededForm`,
    // and this one would land last. Re-read the claim the store already holds instead.
    if (usePublishToCommunityFormStore.getState().seededFormKeys[formKey]) return;
    // Rows minted before the Create tool modal carry neither field, and the name already defaults.
    if (!toolView.description && !toolView.thumbnail) return;

    // Claim the form before awaiting the thumbnail: the modal is already interactive, so a deferred
    // write would land on top of whatever the owner typed meanwhile.
    setFormAction(formKey, {
      name: (toolView.name || workflow.name).slice(0, NAME_MAX),
      description: (toolView.description ?? '').slice(0, DESCRIPTION_MAX),
      thumbnail: null,
      icon: null,
    });
    markFormSeeded(formKey);

    const { thumbnail } = toolView;
    if (!thumbnail) return;

    void (async () => {
      const file = await fetchThumbnailAsFile(thumbnail);
      if (toolSeedCancelled.current || !file) return;
      // Only slots the owner left alone, each with its own id — the form tracks the two separately.
      setFormAction(formKey, (current) => ({
        thumbnail: current.thumbnail ?? { kind: 'new', id: uuidv4(), file },
        icon: current.icon ?? { kind: 'new', id: uuidv4(), file },
      }));
    })();
  }, [
    open,
    hasSeededForm,
    isPublishingAsTool,
    toolView,
    isCommunityMetadataFetched,
    toolListing,
    communityMetadata,
    workflow.name,
    formKey,
    setFormAction,
    markFormSeeded,
  ]);

  const handleGoBack = () => {
    setStep(Step.Details);
  };

  const handleNext = () => {
    if (step === Step.Description) {
      if (!validateDescription()) return;
      trackStepCompleted(Step.Description, Step.Images);
      setStep(Step.Images);
    } else if (step === Step.Images) {
      if (!validateImages()) return;
      trackStepCompleted(Step.Images, Step.Details);
      setStep(Step.Details);
    }
  };

  const handleBack = () => {
    if (step === Step.Images) setStep(Step.Description);
    else if (step === Step.Details) setStep(Step.Images);
  };

  const handlePublish = async () => {
    if (publishInFlightRef.current) return;
    publishInFlightRef.current = true;
    try {
      await runPublish();
    } finally {
      publishInFlightRef.current = false;
    }
  };

  const runPublish = async () => {
    if (!validateDescription()) {
      setStep(Step.Description);
      return;
    }
    if (!validateImages()) {
      setStep(Step.Images);
      return;
    }
    if (!validateDetails()) {
      setStep(Step.Details);
      return;
    }
    if (!form.thumbnail) return;
    if (!figmaConnection?.connected) return;

    trackPublishEvent(WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.SUBMITTED, {
      ...getFormTrackingPayload(),
    });

    // Org publishing stops here, before any Community eligibility check, upload or publish call.
    if (tool && selectedOrg) {
      await runOrgPublish(tool.weaveAppId, selectedOrg.planId);
      return;
    }

    if (isPublishToCommunityEnabled !== true || workflowRole !== 'editor') {
      const eligibility = getCommunityPublishEligibility({
        isFeatureEnabled: isPublishToCommunityEnabled === true,
        workflowRole,
        subscriptionType: currentUser?.activeWorkspace?.subscription?.type,
      });
      trackPublishResult('failure', {
        failureStage: 'eligibility_check',
        eligibilityReason: eligibility.reason,
        featureFlagEnabled: isPublishToCommunityEnabled === true,
        workflowRole,
      });
      updateSnackbarData({
        text: 'You are not eligible to publish to Figma Community.',
        isOpen: true,
      });
      return;
    }

    const latestPublisherProfilesResult = await refetchPublisherProfiles();
    if (latestPublisherProfilesResult.error || !latestPublisherProfilesResult.data) {
      trackPublishResult('failure', {
        failureStage: 'publisher_profiles_refetch',
        ...(latestPublisherProfilesResult.error
          ? getCommunityPublishErrorPayload(latestPublisherProfilesResult.error)
          : {}),
      });
      logger.error('Failed to fetch publisher profiles before publishing:', latestPublisherProfilesResult.error);
      updateSnackbarData({
        text: 'Unable to confirm your Community publishing eligibility. Please try again.',
        isOpen: true,
      });
      return;
    }
    const latestPublisherProfileState = latestPublisherProfilesResult.data;

    const latestEligibility = getCommunityPublishEligibility({
      isFeatureEnabled: isPublishToCommunityEnabled === true,
      workflowRole,
      subscriptionType: currentUser?.activeWorkspace?.subscription?.type,
      isBannedFromCommunityPublishing: latestPublisherProfileState.is_banned_from_community_publishing,
      isPaidFigmaUser: latestPublisherProfileState.is_paid_figma_user,
    });

    if (!latestEligibility.canPublish) {
      trackPublishResult('failure', {
        failureStage: 'eligibility_check',
        eligibilityReason: latestEligibility.reason,
      });
      updateSnackbarData({
        text: 'You are not eligible to publish to Figma Community.',
        isOpen: true,
      });
      return;
    }

    let profile = createdProfile ?? latestPublisherProfileState.profiles[0];
    let handleToClaim = '';
    if (!profile) {
      if (handleStatus.kind !== 'available') {
        trackValidationFailed(Step.Details, ['handle']);
        setErrors((prev) => ({
          ...prev,
          handle: t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.ERRORS.HANDLE_REQUIRED),
        }));
        return;
      }
      handleToClaim = handleStatus.handle;
    }

    const version = ++publishVersionRef.current;
    setStep(Step.Publishing);

    if (!profile) {
      try {
        const created = await createPublisherProfile(handleToClaim);
        if (publishVersionRef.current !== version) return;
        profile = {
          id: created.profile_id,
          name: created.name,
          profile_handle: created.profile_handle,
          entity_type: 'user',
          primary_user_id: created.primary_user_id,
          img_url: created.img_url,
          public_at: new Date().toISOString(),
        };
        setCreatedProfile(profile);
        void refetchPublisherProfiles();
      } catch (error: unknown) {
        trackPublishResult('failure', {
          failureStage: 'profile_create',
          ...getCommunityPublishErrorPayload(error),
        });
        logger.error('Failed to create Community publisher profile', {
          message: error instanceof Error ? error.message : String(error),
        });
        if (publishVersionRef.current !== version) return;
        setErrors((prev) => ({
          ...prev,
          handle: t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.ERRORS.HANDLE_CREATE_FAILED),
        }));
        setStep(Step.Details);
        return;
      }
    }

    // A tool publish is keyed by its `weave_app_id` (= WorkflowTool.id); a workflow publish by the
    // workflow id. Everything downstream (image upload path, publish payload) shares this id.
    const communityResourceId = tool ? tool.weaveAppId : workflow.id;

    let sha1s: string[];
    // Tool-only icon; uploaded in the same batch as the carousel media and sent as `icon_sha1`.
    let iconSha1: string | undefined;
    try {
      ({ sha1s, iconSha1 } = await uploadPublishImages(communityResourceId));
    } catch (error: unknown) {
      trackPublishResult('failure', {
        failureStage: 'image_upload',
        ...getCommunityPublishErrorPayload(error),
      });
      logger.error('Failed to upload community images', {
        message: error instanceof Error ? error.message : String(error),
      });
      if (publishVersionRef.current !== version) return;
      setStep(Step.Failure);
      return;
    }

    if (publishVersionRef.current !== version) return;

    // Drop stale tag ids that don't belong to the selected category.
    const validTagIds = new Set(selectedCategory?.tags?.map((t) => t.id) ?? []);
    const filteredTags = form.tags.filter((id) => validTagIds.has(id));
    const categoryPayload = selectedCategory ? { category_id: selectedCategory.id } : {};
    let frozenVersionNumber: number;

    try {
      const { frozenVersion, draftVersion, updatedAt } = await prepareCommunityPublishSnapshot(
        workflow.id,
        isPublishingAsTool,
      );
      workflow.adoptDraftVersion(draftVersion.version, updatedAt);
      frozenVersionNumber = frozenVersion.version;
    } catch (error: unknown) {
      trackPublishResult('failure', {
        failureStage: 'snapshot_prepare',
        ...getCommunityPublishErrorPayload(error),
      });
      logger.error('Failed to prepare workflow for Community publish', {
        message: error instanceof Error ? error.message : String(error),
      });
      if (publishVersionRef.current !== version) return;
      setStep(Step.Failure);
      return;
    }

    // Both publish responses share these fields; the success handler below only reads them.
    let publishResponse: { resource_id: string; version_id: string; weavy_version_number: number };
    try {
      const commonPayload = {
        name: form.name,
        description: form.description,
        carousel_media: sha1s.map((sha1, i) => ({ sha1, carousel_position: i })),
        weavy_version_number: frozenVersionNumber,
        publisher_profile_id: profile.id,
        ...categoryPayload,
        tags: filteredTags,
        support_contact: form.supportEmail,
        agreed_to_tos: form.agreedToTerms,
      };
      if (isPublishingAsTool) {
        const toolResponse = await publishToolToCommunity({
          weave_app_id: communityResourceId,
          ...commonPayload,
          ...(iconSha1 ? { icon_sha1: iconSha1 } : {}),
        });
        // Seed the pending tool metadata under its own `weave_app_id` key so the panel reflects it immediately.
        seedPendingToolCommunityMetadata(queryClient, communityResourceId, toolResponse);
        publishResponse = toolResponse;
      } else {
        const workflowResponse = await publishWorkflowToCommunity({
          weave_workflow_id: communityResourceId,
          ...commonPayload,
        });
        seedPendingWorkflowCommunityMetadata(queryClient, workflow.id, workflowResponse);
        publishResponse = workflowResponse;
      }
      markFormSeeded(formKey);
    } catch (error: unknown) {
      trackPublishResult('failure', {
        failureStage: 'community_publish',
        weavyVersionNumber: frozenVersionNumber,
        ...getCommunityPublishErrorPayload(error),
      });
      logger.error('Failed to publish to Community', {
        message: error instanceof Error ? error.message : String(error),
      });
      if (publishVersionRef.current !== version) return;
      setStep(Step.Failure);
      return;
    }

    if (isPublishingAsTool) {
      void invalidateToolListingStatus(queryClient, communityResourceId).catch(() => undefined);
      void invalidateToolCommunityMetadata(queryClient, communityResourceId).catch(() => undefined);
    } else {
      void invalidateWorkflowCommunityMetadata(queryClient, workflow.id).catch(() => undefined);
    }

    // The agreement the server just recorded is user-level, so the next publish shows the reminder.
    // Read from the pre-publish refetch, not the render-time snapshot, which may predate the first
    // successful profiles load.
    if (latestPublisherProfileState.community_user_level_creator_agreement_enabled && form.agreedToTerms) {
      void queryClient
        .invalidateQueries({ queryKey: communityPublisherProfilesQueryKey(figmaUserId) })
        .catch(() => undefined);
    }

    if (publishVersionRef.current !== version) return;
    trackPublishResult('success', {
      resourceId: publishResponse.resource_id,
      versionId: publishResponse.version_id,
      weavyVersionNumber: publishResponse.weavy_version_number,
    });
    setStep(Step.Success);
  };

  const runOrgPublish = async (weaveAppId: string, planId: string) => {
    const version = ++publishVersionRef.current;
    setOrgPublishResult(null);
    setStep(Step.Publishing);
    let result: OrgPublishResult | null = null;
    let failureStage: OrgPublishFailureStage = 'image_upload';
    let frozenVersionNumber: number | undefined;

    try {
      // Shrink and upload the new images, and get every image's sha1 in display order
      const { sha1s, iconSha1 } = await uploadPublishImages(weaveAppId);
      // Stop before freezing a version or publishing if the modal was reset meanwhile
      if (publishVersionRef.current !== version) {
        return;
      }

      // Freeze the workflow version the org's members will run
      failureStage = 'snapshot_prepare';
      const { frozenVersion, draftVersion, updatedAt } = await prepareCommunityPublishSnapshot(
        workflow.id,
        true,
        'org',
      );
      workflow.adoptDraftVersion(draftVersion.version, updatedAt);
      if (publishVersionRef.current !== version) {
        return;
      }

      // Publish to the picked org
      failureStage = 'org_publish';
      frozenVersionNumber = frozenVersion.version;
      result = await publishToolToOrg({
        weaveAppId,
        planId,
        toolName: form.name,
        weavyVersionNumber: frozenVersion.version,
        carouselSha1s: sha1s,
        description: form.description,
        iconSha1,
      });
    } catch (error) {
      trackPublishResult('failure', {
        failureStage,
        ...(frozenVersionNumber === undefined ? {} : { weavyVersionNumber: frozenVersionNumber }),
        ...getCommunityPublishErrorPayload(error),
      });
      logger.error('Failed to publish tool to org:', error);
      if (publishVersionRef.current !== version) {
        return;
      }
      setStep(Step.Failure);
      return;
    }

    if (publishVersionRef.current !== version) {
      return;
    }
    void invalidateToolListingStatus(queryClient, weaveAppId).catch(() => undefined);
    trackPublishResult('success', { weavyVersionNumber: frozenVersionNumber });
    setOrgPublishResult(result);
    setStep(Step.Success);
  };

  /** Uploads the new images and returns every image's sha1 in display order, plus the tool icon's. */
  const uploadPublishImages = async (resourceId: string): Promise<{ sha1s: string[]; iconSha1?: string }> => {
    const orderedImages = [form.thumbnail, ...(selectedOrg ? [] : form.carouselImages)].filter(
      (image): image is PublishImage => image != null,
    );
    const iconImage = isPublishingAsTool ? form.icon : null;

    // Shrink the new images one at a time, icon last, so the uploaded sha1s line up below
    const newFiles: File[] = [];
    for (const image of orderedImages) {
      if (image.kind === 'new') {
        newFiles.push(await shrinkImageToFit(image.file));
      }
    }
    if (iconImage?.kind === 'new') {
      newFiles.push(await shrinkImageToFit(iconImage.file, { maxLongEdge: ICON_MAX_LONG_EDGE, preserveAlpha: true }));
    }

    // Upload under the tool's or the workflow's id
    const uploadedSha1s = isPublishingAsTool
      ? await uploadCommunityToolImages(newFiles, resourceId)
      : await uploadCommunityImages(newFiles, resourceId);

    // Existing images keep their sha1; new ones take the uploaded sha1s in order
    let nextUpload = 0;
    const sha1s = orderedImages.map((image) => (image.kind === 'existing' ? image.sha1 : uploadedSha1s[nextUpload++]));
    if (!iconImage) {
      return { sha1s };
    }
    return { sha1s, iconSha1: iconImage.kind === 'existing' ? iconImage.sha1 : uploadedSha1s[nextUpload++] };
  };

  const title = t(
    isPublishingAsTool
      ? I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.TITLE_PUBLISH_TOOL
      : I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.TITLE_PUBLISH,
  );
  const publisherName = createdProfile?.name || publisherProfileState.profiles[0]?.name || '';

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      disablePortal
      slotProps={{
        paper: {
          sx: {
            width: 648,
            maxWidth: 648,
            background: token.bg,
            overflow: 'hidden',
          },
        },
        transition: {
          onExited: resetState,
        },
      }}
    >
      <AppDialogSection>
        <AppDialogHeader
          component="div"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 3,
            borderBottom: `1px solid ${token.border}`,
          }}
        >
          <AppDialogTitle>{title}</AppDialogTitle>
          <AppDialogXBtn onClick={handleClose} size={20} />
        </AppDialogHeader>

        <AppDialogContent sx={{ p: 0 }}>
          {isTerminalStep(step) ? (
            <FlexCol sx={{ height: 510 }}>
              <PublishToCommunityModalTerminal
                state={step}
                isPublishingAsTool={isPublishingAsTool}
                thumbnailPreviewUrl={thumbnailPreviewUrl}
                workflowName={form.name}
                publisherName={publisherName}
                onCopyLink={() => void copyLink()}
                onGoToCommunity={goToCommunity}
                onGoBack={handleGoBack}
                communityLinkReady={Boolean(communityMetadata?.community_url)}
                orgName={selectedOrg?.name}
                orgPublisher={orgPublishResult?.publisher ?? null}
                onCopyOpenInFigmaLink={() => void copyOpenInFigmaLink()}
                onOpenInFigma={openInFigma}
                openInFigmaLinkReady={Boolean(openInFigmaUrl)}
              />
            </FlexCol>
          ) : (
            <Flex sx={{ height: 430 }}>
              <FlexCol
                sx={{
                  width: 213,
                  p: '24px 18px',
                  gap: 0.5,
                  borderRight: `1px solid ${token.border}`,
                  flexShrink: 0,
                }}
              >
                <SidebarItem
                  index={1}
                  label={t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.STEPS.DESCRIPTION)}
                  active={step === Step.Description}
                  onClick={() => setStep(Step.Description)}
                />
                <SidebarItem
                  index={2}
                  label={t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.STEPS.IMAGES)}
                  active={step === Step.Images}
                  onClick={() => setStep(Step.Images)}
                />
                <SidebarItem
                  index={3}
                  label={t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.STEPS.DETAILS)}
                  active={step === Step.Details}
                  onClick={() => setStep(Step.Details)}
                />
              </FlexCol>

              <FlexCol
                ref={scrollContainerRef}
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  scrollbarGutter: 'stable',
                  scrollbarWidth: 'thin',
                  '&::-webkit-scrollbar': { width: '8px' },
                }}
              >
                <FlexCol sx={{ pl: 3, pr: 2, py: 3, gap: 3 }}>
                  {step === Step.Description && (
                    <PublishToCommunityModalDescription
                      name={form.name}
                      onNameChange={handleNameChange}
                      nameError={errors.name}
                      description={form.description}
                      onDescriptionChange={handleDescriptionChange}
                      descriptionError={errors.description}
                      category={form.category}
                      onCategoryChange={(v) => {
                        if (v === form.category) return;
                        userSetForm({ category: v, tags: [] });
                        clearError('category');
                      }}
                      categoryError={errors.category}
                      categories={categories}
                      categoriesLoading={categoriesLoading}
                      categoryRequired={categoryRequired}
                      tags={form.tags}
                      onToggleTag={toggleTag}
                      isPublishingAsTool={isPublishingAsTool}
                      isOrgPublish={Boolean(selectedOrg)}
                      orgName={selectedOrg?.name}
                    />
                  )}
                  {step === Step.Images && (
                    <PublishToCommunityModalImages
                      isPublishingAsTool={isPublishingAsTool}
                      isOrgPublish={Boolean(selectedOrg)}
                      thumbnailPreviewUrl={thumbnailPreviewUrl}
                      onThumbnailFile={handleThumbnailFile}
                      onThumbnailRemove={handleThumbnailRemove}
                      iconPreviewUrl={iconPreviewUrl}
                      iconRequired={iconRequired}
                      onIconFile={handleIconFile}
                      onIconRemove={handleIconRemove}
                      imageError={errors.image}
                      iconError={errors.icon}
                      carouselImages={form.carouselImages}
                      carouselPreviewUrls={carouselPreviewUrls}
                      onCarouselFile={handleCarouselFile}
                      onCarouselRemove={handleCarouselRemove}
                      onCarouselReorder={handleCarouselReorder}
                    />
                  )}
                  {step === Step.Details && (
                    <PublishToCommunityModalDetails
                      supportEmail={form.supportEmail}
                      onSupportEmailChange={(v) => setField('supportEmail', v)}
                      agreedToTerms={form.agreedToTerms}
                      requiresCreatorAgreement={requiresCreatorAgreement}
                      onAgreedToTermsChange={handleAgreedToTermsChange}
                      termsError={errors.terms}
                      onTermsClick={handleTermsClick}
                      profile={createdProfile ?? publisherProfileState.profiles[0] ?? null}
                      userEmail={figmaEmail}
                      userDisplayName={currentUser?.displayName ?? ''}
                      userAvatarUrl={currentUser?.photoURL ?? ''}
                      handle={handle}
                      onHandleChange={(v) => {
                        setHandle(v);
                        clearError('handle');
                      }}
                      onHandleStatusChange={setHandleStatus}
                      handleError={errors.handle}
                      isPublishingAsTool={isPublishingAsTool}
                      isOrgPublish={selectedOrg !== null}
                      orgOptions={orgDestinations}
                      selectedOrgPlanId={selectedOrg?.planId}
                      onSelectedOrgPlanIdChange={setPickedOrgPlanId}
                    />
                  )}
                </FlexCol>
              </FlexCol>
            </Flex>
          )}
        </AppDialogContent>

        {!isTerminalStep(step) && (
          <AppDialogActions
            sx={{
              justifyContent: 'space-between',
              gap: 2,
              p: 3,
              borderTop: `1px solid ${token.border}`,
            }}
          >
            <FlexCenVer sx={{ gap: 1, minWidth: 0 }}>
              <InfoIcon style={{ flexShrink: 0, color: token.textSecondary }} />
              {selectedOrg ? (
                <TextSmRg
                  sx={{
                    color: token.textSecondary,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ORG_DESTINATION.FOOTER_INFO, {
                    orgName: selectedOrg.name,
                  })}
                </TextSmRg>
              ) : (
                <TextSmRg sx={{ color: token.textSecondary, maxWidth: 244 }}>
                  {t(
                    isPublishingAsTool
                      ? I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.FOOTER.INFO_TOOL
                      : I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.FOOTER.INFO,
                  )}
                </TextSmRg>
              )}
            </FlexCenVer>
            <FlexCenVer sx={{ gap: 1, flexShrink: 0 }}>
              {step === Step.Description ? (
                <ButtonContained mode="outlined" size="small" onClick={handleClose}>
                  {t(I18N_KEYS.GENERAL.CANCEL)}
                </ButtonContained>
              ) : (
                <ButtonContained mode="outlined" size="small" onClick={handleBack}>
                  {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.FOOTER.BACK)}
                </ButtonContained>
              )}
              {step === Step.Details ? (
                <ButtonContained mode="filled-light" size="small" onClick={() => void handlePublish()}>
                  {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.FOOTER.PUBLISH)}
                </ButtonContained>
              ) : (
                <ButtonContained mode="filled-light" size="small" onClick={handleNext}>
                  {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.FOOTER.NEXT)}
                </ButtonContained>
              )}
            </FlexCenVer>
          </AppDialogActions>
        )}
      </AppDialogSection>
    </AppDialog>
  );
};

const SidebarItem = ({
  index,
  label,
  active,
  onClick,
}: {
  index: number;
  label: string;
  active: boolean;
  onClick: () => void;
}) => (
  <FlexCenVer
    onClick={onClick}
    sx={{
      gap: 1,
      p: 1,
      borderRadius: 1,
      cursor: 'pointer',
      background: active ? token.bgTransparentSecondary : 'transparent',
      '&:hover': { background: active ? token.bgTransparentSecondary : token.bgTransparent },
    }}
  >
    <FlexCenVer
      sx={{
        width: 16,
        height: 16,
        borderRadius: '3px',
        justifyContent: 'center',
        background: active ? token.bgInverse : token.bgTransparent,
        color: active ? token.textOninverse : token.text,
        fontSize: 9,
        fontWeight: 550,
        fontFamily: 'Inter, sans-serif',
        flexShrink: 0,
      }}
    >
      {index}
    </FlexCenVer>
    <TextSmRg>{label}</TextSmRg>
  </FlexCenVer>
);

export default PublishToCommunityModal;
